import { useMemo, useState } from 'react';

import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { menuIonStyle } from '@/pages/layouts/getStartedModal/gettingStarted_KeyboardShortcuts/types.ts';
import { Icon } from '@/utils';

import type { Hotkey } from '../hotkeys';
import { HotkeyBase, LayoutPickerHotkeyNames } from '../hotkeys/types.ts';
import { DefaultLayoutPickerHotKeys } from './useLayoutPickerHotKeysDefinitions/defaultHotKeys.ts';

type LayoutPickerHotkeyUi = Pick<
  Hotkey,
  'description' | 'menuIconForAction' | 'menuIconBackground' | 'disabled'
>;

export const useLayoutPickerHotKeysDefinitions = (): Record<LayoutPickerHotkeyNames, Hotkey> => {
  const { t } = useTranslation(['common']);
  const [resolvedLayoutPickerHotKeys] = useState<Record<LayoutPickerHotkeyNames, HotkeyBase>>(
    DefaultLayoutPickerHotKeys
  );

  return useMemo((): Record<LayoutPickerHotkeyNames, Hotkey> => {
    const layoutPickerHotkeyUi: Record<LayoutPickerHotkeyNames, LayoutPickerHotkeyUi> = {
      [LayoutPickerHotkeyNames.FocusSearchKey]: {
        description: t('common:shortcuts.layoutPicker.focusSearch'),
        menuIconForAction: <Search style={menuIonStyle} />,
      },
      [LayoutPickerHotkeyNames.NewLayoutKey]: {
        description: t('common:shortcuts.layoutPicker.newLayout'),
        menuIconForAction: <Icon.CirclePlus size='large' style={menuIonStyle} />,
      },
      [LayoutPickerHotkeyNames.NewLayoutFromTemplateKey]: {
        description: t('common:shortcuts.layoutPicker.newLayoutFromTemplate'),
        menuIconForAction: <Icon.Grid3x3 style={menuIonStyle} />,
      },
    };

    const result = {} as Record<LayoutPickerHotkeyNames, Hotkey>;
    for (const name of Object.values(LayoutPickerHotkeyNames) as LayoutPickerHotkeyNames[]) {
      result[name] = {
        ...resolvedLayoutPickerHotKeys[name],
        ...layoutPickerHotkeyUi[name],
      };
    }
    return result;
  }, [t, resolvedLayoutPickerHotKeys]);
};
